import React, { useState } from 'react';
import { Printer, FileText, AlertCircle } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import { getConfig } from '../services/config';
import { useLanguage } from '../contexts/LanguageContext';

const BatchPrinting = () => {
  const { t } = useLanguage();
  const [batchNo, setBatchNo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  const handlePrintBatch = async () => {
    if (!batchNo.trim()) {
      showError('Please enter a Batch No');
      return;
    }

    try {
      setIsLoading(true);
      const response = await MainService.PrintByBatchNo(batchNo.trim());
      
      if (response) {
        // Open the PDF in a new tab
        const pdfUrl = `${getConfig().API_BASE_URL}ReportFiles/${response}`;
        window.open(pdfUrl, '_blank');
        showSuccess('PDF generated successfully');
      } else {
        showError('Print Error - No data received');
      }
    } catch (error) {
      console.error('Print Error:', error);
      showError('Print Error - Failed to generate PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePrintBatch();
    }
  };

  return (
    <div className="gradient-bg page-container">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('batch.title')}</h1>
          <p className="text-gray-600">{t('batch.subtitle')}</p>
        </div>

        {/* Main Content */}
        <div className="gradient-card">
          <div className="p-8">
            {/* Input Section */}
            <div className="mb-8">
              <label htmlFor="batchNo" className="block text-sm font-medium text-gray-700 mb-2">
                {t('batch.batchNumber')}
              </label>
              <div className="flex items-end space-x-4">
                <div className="flex-1">
                  <input
                    id="batchNo"
                    type="text"
                    value={batchNo}
                    onChange={(e) => setBatchNo(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('batch.placeholder')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={handlePrintBatch}
                    disabled={isLoading || !batchNo.trim()}
                    className="btn-primary flex items-center space-x-2 px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Printer className="w-5 h-5" />
                        <span>{t('batch.printBatch')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <FileText className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">{t('batch.helpTitle')}</h3>
                  <ul className="text-green-800 space-y-2">
                  <li className="flex items-start space-x-2">
                      <span className="text-green-600 font-bold">1.</span>
                      <span>{t('batch.step1')}</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-green-600 font-bold">2.</span>
                      <span>{t('batch.step2')}</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-green-600 font-bold">3.</span>
                      <span>{t('batch.step3')}</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-green-600 font-bold">4.</span>
                      <span>{t('batch.step4')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Error State */}
            {!batchNo.trim() && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-800">{t('batch.hint')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            {t('batch.support')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BatchPrinting;
